/**
 * gamificationCache
 * ------------------------------------------------------------------
 * Coalesces /v2/gamification/business/{id}/score requests fired by the many
 * <BusinessBadgesInline> / <BusinessLevelBadge> instances on listing pages.
 *
 * Without this module each card mounts and fires its own GET, so a 50-card
 * Explorar page produced ~100 individual requests (one per card × two badge
 * components per card). With this module:
 *
 *   1. Repeated calls for the same id share the same in-flight Promise.
 *   2. All calls scheduled within the same micro-window (16 ms) are flushed
 *      together via POST /v2/gamification/scores/bulk.
 *   3. Resolved scores are stored in `sessionStorage` (key: badges_<id>) so a
 *      re-render or back-navigation reuses them without any network call.
 */
import { api } from './api';

const STORAGE_KEY_PREFIX = 'gscore_';
const FLUSH_DELAY_MS = 16;
const memoryCache = new Map(); // id -> resolved score data
const pending = new Map(); // id -> { resolve, reject }
let flushTimer = null;

function readSessionCache(id) {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${id}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeSessionCache(id, data) {
  try {
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${id}`, JSON.stringify(data));
  } catch {
    /* quota or privacy mode — ignore */
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

async function flush() {
  flushTimer = null;
  if (pending.size === 0) return;
  // Snapshot and clear before the network call so new calls re-batch cleanly.
  const ids = Array.from(pending.keys());
  const callbacks = new Map(pending);
  pending.clear();

  try {
    const res = await api.post('/v2/gamification/scores/bulk', {
      business_ids: ids,
    });
    const map = res.data || {};
    callbacks.forEach((cb, id) => {
      const data = map[id] || null;
      if (data) {
        memoryCache.set(id, data);
        writeSessionCache(id, data);
      }
      cb.resolve(data);
    });
  } catch (err) {
    callbacks.forEach((cb) => cb.reject(err));
  }
}

/**
 * Returns a Promise that resolves to the score document for `businessId`, or
 * `null` if the backend has no cached score for it.
 */
export function getScore(businessId) {
  if (!businessId) return Promise.resolve(null);

  if (memoryCache.has(businessId)) {
    return Promise.resolve(memoryCache.get(businessId));
  }
  const fromSession = readSessionCache(businessId);
  if (fromSession) {
    memoryCache.set(businessId, fromSession);
    return Promise.resolve(fromSession);
  }
  if (pending.has(businessId)) {
    // Another caller already queued this id — share its promise.
    return new Promise((resolve, reject) => {
      const existing = pending.get(businessId);
      const prevResolve = existing.resolve;
      const prevReject = existing.reject;
      pending.set(businessId, {
        resolve: (v) => { prevResolve(v); resolve(v); },
        reject: (e) => { prevReject(e); reject(e); },
      });
    });
  }
  return new Promise((resolve, reject) => {
    pending.set(businessId, { resolve, reject });
    scheduleFlush();
  });
}
